import { z } from "zod";

export const BucketValueTypeSchema = z.enum([
  "Money",
  "Text",
  "Boolean",
  "Decimal",
]);
export type BucketValueType = z.infer<typeof BucketValueTypeSchema>;

export const ComponentParameterSchema = z.object({
  bucketTypeId: z.number(),
  parameterQualifiedName: z.string().nullable(),
  elementQualifiedName: z.string(),
  displayName: z.string(),
  bucketValueType: BucketValueTypeSchema,
  isSealed: z.boolean(),
  // When true, this parameter accepts ONLY a reference-lookup value
  // (`referenceValue`); a plain `value` is rejected server-side as
  // `PAYCOMP_VALUE_OR_REFERENCE_REQUIRED`. The value/referenceValue XOR is
  // resolved server-side from the raw `columns` + mappings, so the Web never
  // posts `parameterValues`; this flag is UI/validation surfacing only,
  // labelling the parameter on the mapping screen. Parsed tolerantly (optional)
  // so fixture responses that pre-date the flag still validate (absent → not
  // reference-required).
  requiresReferenceValue: z.boolean().optional(),
  // ── Discovery envelope (LADR-038 / #892) ──────────────────────────────────
  // These describe the *component instance* the parameter belongs to, not the
  // parameter itself. `useComponentsParameters` keys the discovery response by
  // component name, so every parameter on a given component carries the same
  // trio. They are parsed tolerantly (nullish) so fixture-backed responses that
  // omit the envelope, and live Christmas/API responses that emit explicit
  // `null`, both validate; downstream consumers treat absent/null as "not ambiguous".
  //
  // `companyComponentId` disambiguates an ambiguous component name at the wire
  // boundary (see D4): when `isAmbiguous` is true, a row MUST carry this id or
  // the server rejects it with `PAYCOMP_COMPONENT_AMBIGUOUS`.
  companyComponentId: z.string().nullish(),
  isAmbiguous: z.boolean().nullish(),
  isIndependentlyAssignable: z.boolean().nullish(),
});
export type ComponentParameter = z.infer<typeof ComponentParameterSchema>;
